import { useGetCashToClose } from 'hooks/useGetCashToClose';
import { CashToCloseData, CashToCloseDataEntry } from 'hooks/useGetCashToClose/types';
import { useUpdateCashToClose } from 'hooks/ledger/mutations';
import { useAccountingUpdateSocketEvent } from "hooks/useAccountingUpdateFromSocket";
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { asyncForEach } from 'utils/helpers';
import { normalizeApiError } from 'utils/api-error';

import { initialData } from './utils/initialData';

export const useCashToClose = () => {
  const { t } = useTranslation();
  const { id: orderId } = useParams<{ id: string }>();
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false);
  const queryCache = useQueryClient();
  const showAlert = useAlert();

  useAccountingUpdateSocketEvent({
    callback: () => queryCache.invalidateQueries(['accounting-balance-cash-to-close', orderId])
  });

  const cashToCloseForm = useForm<CashToCloseData>({
    defaultValues: initialData,
    mode: 'onChange',
  });

  const [orderTransactionType, setOrderTransactionType] = useState<string>('');

  const { isLoading: isCashToCloseLoading, isFetching: isCashToCloseFetching } = useGetCashToClose({
    orderId,
    queryConfig: {
      refetchOnWindowFocus: false,
      onSuccess: ({ cashToCloseData, configuration }) => {
        cashToCloseForm.reset(cashToCloseData);
        setOrderTransactionType(configuration.transaction_type);
      },
    },
  });

  useEffect(() => {
    if (isCashToCloseLoading || isCashToCloseFetching) {
      showAlert('Getting new information', 'info');
    }
  }, [isCashToCloseLoading, isCashToCloseFetching, showAlert]);

  const { mutateAsync: updateCashToClose } = useUpdateCashToClose({});

  const {
    formState: { dirtyFields, isDirty },
  } = cashToCloseForm;
  const onSave = cashToCloseForm.handleSubmit(async (data) => {
    if (isDirty) {
      setIsUpdateLoading(true);
      try {
        await asyncForEach(Object.keys(dirtyFields), (propertyKey) => {
          const field: CashToCloseDataEntry = data[propertyKey] as CashToCloseDataEntry;
          return updateCashToClose({
            orderId,
            entryCode: field.entry_code,
            data: {
              estimate_amount: field.estimate_amount,
              changed: field.changed,
              changed_text: field.changed_text,
            },
          });
        });

        try {
          await queryCache.invalidateQueries(
            ['accounting-balance-cash-to-close', orderId],
            {},
            { throwOnError: true }
          );
        } catch (refetchError) {
          const errorMessage = normalizeApiError(refetchError);
          showAlert(errorMessage, 'error');
          return;
        }

        queryCache.invalidateQueries(['party-order', orderId]);
        queryCache.invalidateQueries(['orderById', orderId]);
        showAlert('All the changes were successfully saved', 'success');
      } catch (mutationError) {
        const errorMessage = normalizeApiError(mutationError);
        showAlert(errorMessage, 'error');
      } finally {
        setIsUpdateLoading(false);
      }
    }
  });

  return {
    t,
    isCashToCloseLoading,
    isUpdateLoading,
    orderTransactionType,
    cashToCloseForm,
    onSave,
  };
};
